import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Text
} from '@chakra-ui/react'
import { CustomCollapse } from 'BrokerTsLib/src/components'
import type {
  InsuranceParamsPropertyDto,
  PolicyHolderDto,
  Property
} from 'BrokerTsLib/src/dtos'
import {
  Insurer,
  Product,
  Selection,
  SelectionType
} from 'BrokerTsLib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useMemo } from 'react'

import { Panel } from '@/components/Panel'
import { BCNOptions } from '@/containers/BCNOptions'
import { ICOOptions } from '@/containers/ICOOptions'
import { InsureeAddressRuian } from '@/containers/InsureeAddressRuian'
import { InsureeName } from '@/containers/InsureeName'
import { InsureeType } from '@/containers/InsureeType'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'

import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { FormGroup } from '../../../components/FormGroup'
import { FormLabel } from '../../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../../components/FormSection'
import { FormSpacer } from '../../../components/FormSpacer'
import { RadioStack } from '../../../components/RadioStack'
import {
  useInsurers,
  useSelections
} from '../../../hooks'
import { KNButton } from './Step1Form'

export function PolicyHolderForm() {
  const nameof = useNameof<InsuranceParamsPropertyDto>()
  const pnameof = useNameof<Property>(nameof((x) => x.property))
  const phnameof = useNameof<PolicyHolderDto>(nameof((x) => x.policyHolder))

  const { product } = useApp()
  const { isInsurerAvailable } = useInsurers(false, null)
  const { data: dataVztah } = useSelections(SelectionType.VztahKPremetuPojisteni)

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    control
  } = methods

  const isContracted = !!getValues('generalParams.contractNumber')
  const phType = watch('policyHolder.person.type')
  const propRelationId = watch('property.data.propertyRelationId')
  const insuredPersonSameAs = watch('policyHolder.insuredPersonSameAs')
  const insuredPersonType = watch('policyHolder.insuredPerson.type')

  const sameAsOptions = useMemo(
    () => [
      {
        label: 'Stejný s pojistníkem',
        value: Selection.PersonSameAsPolicyHolder,
      },
      { label: 'Jiný', value: null },
    ],
    [],
  )

  return (<FormSection>
    <FormSectionHeader>
      <FormSectionTitle>Informace o pojistníkovi</FormSectionTitle>

      <FormSectionDescription>
        Zadejte informace o pojistníkovi.
      </FormSectionDescription>
    </FormSectionHeader>

    <FormSectionBody>
      <InsureeType personPrefix={phnameof(x => x.person)} disabled={isContracted} />

      <FormSpacer />

      {isIn(phType, Selection.Person, Selection.SelfEmployed) && <BCNOptions personPrefix={phnameof(x => x.person)} disabled={isContracted} />}
      {isIn(phType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={phnameof(x => x.person)} disabled={isContracted} />}

      <FormGroup>
        {isIn(phType, Selection.Person, Selection.SelfEmployed) && <InsureeName personPrefix={phnameof(x => x.person)} showTitles isDisabled={isContracted} />}
      </FormGroup>

      {product == Product.LiabilityInsurance &&
                <>
                  {/* <FormSpacer /> */}

                  <InsureeAddressRuian
                    addressPrefix={phnameof((x) => x.person.address)}
                    isDisabled={isContracted}
                  />
                </>
      }

      {product == Product.PropertyInsurance &&
                <>
                  <FormSpacer />

                  <FormControl>
                    <FormLabel tooltip="Určení vztahu k předmětu pojištění a také způsobu, jak je majetek užíván.">
                      Vztah k předmětu pojištění
                    </FormLabel>

                    <RadioGroup
                      name={register('property.data.propertyRelationId').name}
                      value={watch('property.data.propertyRelationId')?.toString()}
                      onChange={val => setValue('property.data.propertyRelationId', Number(val))}
                      isDisabled={isContracted}
                    >
                      <RadioStack variant="stackedTile">
                        {dataVztah?.map(item => <Radio value={item.id.toString()} key={item.id.toString()}>
                          {item.description}
                        </Radio>)}
                      </RadioStack>
                    </RadioGroup>
                  </FormControl>

                  <CustomCollapse in={isInsurerAvailable(Insurer.Pillow) && propRelationId == Selection.Pronajimatel}>
                    <FormControl isRequired isInvalid={!!errors.property?.data?.longTimeRent}>
                      <FormLabel>Je majetek pronajímán dlouhodobě?</FormLabel>

                      <RadioGroup isDisabled={isContracted} value={watch('property.data.longTimeRent') == true ? 'yes' : watch('property.data.longTimeRent') == false ? 'no' : 'yes'} onChange={val => setValue('property.data.longTimeRent', val === 'yes')}>
                        <RadioStack variant="stackedTile">
                          <Radio value="yes">Ano</Radio>
                          <Radio value="no">Ne</Radio>
                        </RadioStack>
                      </RadioGroup>

                      {errors.property?.data?.longTimeRent && <FormErrorMessage>
                        {errors.property?.data?.longTimeRent.message}
                      </FormErrorMessage>}
                    </FormControl>
                  </CustomCollapse>

                  <CustomCollapse in={isInsurerAvailable(Insurer.GeneraliCeskaPojistovna) && isIn(phType, Selection.Company, Selection.SelfEmployed)}>
                    <FormSpacer />

                    <FormGroup>
                      <Box>
                        <Text fontWeight="bold" mr={1}>
                          Splňuje pojistník alespoň 2 ze 3 níže uvedených podmínek?
                        </Text>
                        <List>
                          <ListItem>
                            Je čistý obrat min. 13 600 000 EUR (cca 340 000 000 Kč)?
                          </ListItem>
                          <ListItem>
                            Je úhrn rozvahy min. 6 600 000 EUR (cca 165 000 000 Kč)?
                          </ListItem>
                          <ListItem>
                            Je průměrný roční stav zaměstnanců min. 250?
                          </ListItem>
                        </List>
                      </Box>

                      <FormControl isInvalid={!!errors.property?.data?.bigRiskCompany}>
                        <RadioGroup value={watch('property.data.bigRiskCompany') == true ? 'yes' : watch('property.data.bigRiskCompany') == false ? 'no' : undefined} onChange={val => setValue('property.data.bigRiskCompany', val === 'yes')} isDisabled={isContracted}>
                          <RadioStack variant="stackedTile">
                            <Radio value="yes">Ano</Radio>
                            <Radio value="no">Ne</Radio>
                          </RadioStack>
                        </RadioGroup>

                        {errors.property?.data?.bigRiskCompany && <FormErrorMessage>
                          {errors.property?.data?.bigRiskCompany.message}
                        </FormErrorMessage>}
                      </FormControl>
                    </FormGroup>
                  </CustomCollapse>
                </>
      }

      <FormSpacer />
      <FormSpacer />

      {
        /* InsuredPerson */
      }
      <FormControl>
        <FormLabel>Pojištěný</FormLabel>

        <Flex gap={8}>
          <Controller name="policyHolder.insuredPersonSameAs" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
            <RadioStack variant="stackedTile">
              {sameAsOptions.map(item => <Radio key={item.value} value={item.value?.toString() ?? ''}>
                {item.label}
              </Radio>)}
            </RadioStack>
          </RadioGroup>} />

          {product == Product.PropertyInsurance &&
                        <KNButton />
          }
        </Flex>
      </FormControl>

      <CustomCollapse in={!insuredPersonSameAs}>
        <Panel>
          <InsureeType personPrefix={phnameof(x => x.insuredPerson)} disabled={isContracted} />

          <FormSpacer />

          {isIn(insuredPersonType, Selection.Person, Selection.SelfEmployed) && <BCNOptions personPrefix={phnameof(x => x.insuredPerson)} disabled={isContracted} />}
          {isIn(insuredPersonType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={phnameof(x => x.insuredPerson)} disabled={isContracted} />}

          {isIn(insuredPersonType, Selection.Person, Selection.SelfEmployed) && <>
            <FormGroup>
              <InsureeName personPrefix={phnameof(x => x.insuredPerson)} isDisabled={isContracted} showTitles />
            </FormGroup>
          </>}

          {product == Product.LiabilityInsurance &&
                        <>
                          {/* <FormSpacer /> */}

                          <InsureeAddressRuian
                            addressPrefix={phnameof((x) => x.insuredPerson!.address)}
                            isDisabled={isContracted}
                          />
                        </>
          }
        </Panel>
      </CustomCollapse>
    </FormSectionBody>
  </FormSection>)
}
